/*
 * SoleThreads — "Talk to the founder" feedback popup (snippets/solethreads-founder-feedback.liquid)
 *
 * <solethreads-founder-feedback> wraps one native <dialog> with the feedback form.
 *   - Opened by the founder section's button ([aria-controls] = the dialog id). Closes on the ×
 *     button, a backdrop click, or Escape; the page behind does not scroll while it is open.
 *   - Fields are validated here (with the browser's own checks off) so messages are consistent.
 *   - Messages are sent with EmailJS (emailjs.send, service/template/public key from the snippet), whose
 *     template sets the recipient. Success is shown only when EmailJS confirms it; otherwise an error,
 *     and the message stays in the form to try again. Nothing is posted to Shopify, so there is no
 *     second copy and no fallback to another address.
 *   - The EmailJS SDK (pinned, checked with its hash) is loaded on the first open.
 *   - Safeguards: one send at a time (button disabled while sending, and a click right after a failed
 *     send is ignored, so a double-click doesn't retry at once), a hidden spam-trap field, a minimum
 *     time between opening and sending, EmailJS's headless-browser block, and at most one message per
 *     THROTTLE per browser. EmailJS's own limitRate isn't used: it also counts failed sends, which would
 *     stop a visitor from simply trying again.
 */
(() => {
  const TAG = "solethreads-founder-feedback";
  if (customElements.get(TAG)) return;

  const OPEN_CLASS = "st-ffb-open";
  // Deliberately simple: something@something.tld, no spaces.
  const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  // A person can't open the popup and write a message faster than this.
  const MIN_FILL_TIME = 2500;
  // Minimum time between two sent messages from the same browser.
  const THROTTLE = 30000;
  const THROTTLE_KEY = "st-ffb-last-sent";
  // Clicks this soon after a failed send are the rest of a double-click, not a retry.
  const RETRY_GAP = 1000;
  const TEXT = {
    required: {
      name: "Please enter your name.",
      email: "Please enter your email address.",
      message: "Please write a message to the founder.",
    },
    invalidEmail: "Please enter a valid email address, like name@example.com.",
    fixErrors: "Please fix the highlighted fields.",
    sending: "Sending…",
    sendingStatus: "Sending your message.",
    sentStatus: "Your message has been sent.",
    failed: "We couldn't send your message. Please check your connection and try again.",
    unavailable: "We couldn't send your message right now. Please try again in a few minutes.",
    tooSoon: "You've just sent a message. Please wait a moment before sending another.",
  };

  // When this browser last sent a message (per tab if storage is unavailable)
  let lastSentFallback = 0;
  const lastSent = () => {
    try {
      return Number(window.localStorage.getItem(THROTTLE_KEY)) || 0;
    } catch {
      return lastSentFallback;
    }
  };
  const rememberSent = () => {
    lastSentFallback = Date.now();
    try {
      window.localStorage.setItem(THROTTLE_KEY, String(lastSentFallback));
    } catch {}
  };

  // The SDK, loaded once per page and shared by every popup
  let sdk = null;
  const loadSdk = (src, integrity) => {
    if (window.emailjs) return Promise.resolve(window.emailjs);
    if (!sdk) {
      sdk = new Promise((resolve, reject) => {
        const script = document.createElement("script");
        script.src = src;
        if (integrity) {
          script.integrity = integrity;
          script.crossOrigin = "anonymous";
        }
        script.async = true;
        script.onload = () => (window.emailjs ? resolve(window.emailjs) : reject(new Error("EmailJS not defined")));
        script.onerror = () => reject(new Error("EmailJS SDK failed to load"));
        document.head.appendChild(script);
      }).catch((error) => {
        sdk = null; // try again on the next send
        throw error;
      });
    }
    return sdk;
  };

  class SolethreadsFounderFeedback extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector("[data-ffb-dialog]");
      this.form = this.dialog && this.dialog.querySelector("form");
      if (!this.form) return;

      this.events = new AbortController();
      const { signal } = this.events;

      this.formView = this.form.querySelector("[data-ffb-form-view]");
      this.successView = this.form.querySelector("[data-ffb-success-view]");
      this.successTitle = this.form.querySelector("[data-ffb-success-title]");
      this.alert = this.form.querySelector("[data-ffb-alert]");
      this.status = this.form.querySelector("[data-ffb-status]");
      this.submitButton = this.form.querySelector("[data-ffb-submit]");
      this.submitLabel = this.form.querySelector("[data-ffb-submit-label]");
      this.trap = this.form.querySelector("[data-ffb-trap]");
      this.inputs = [...this.form.querySelectorAll("[data-ffb-input]")];
      this.validated = false;
      this.sending = false;

      // Validation is handled here
      this.form.noValidate = true;

      document.querySelectorAll(`[aria-controls="${this.dialog.id}"]`).forEach((trigger) => {
        trigger.addEventListener("click", (event) => {
          event.preventDefault();
          this.open(trigger);
        }, { signal });
      });

      this.dialog.querySelectorAll("[data-ffb-close]").forEach((button) =>
        button.addEventListener("click", () => this.dialog.close(), { signal })
      );

      // The panel fills the dialog, so the dialog itself is only the target on the backdrop.
      // Both ends of the click must be there, so a text selection dragged outside doesn't close it.
      this.dialog.addEventListener("pointerdown", (event) => {
        this.pressedBackdrop = event.target === this.dialog;
      }, { signal });
      this.dialog.addEventListener("click", (event) => {
        if (event.target === this.dialog && this.pressedBackdrop) this.dialog.close();
        this.pressedBackdrop = false;
      }, { signal });

      this.dialog.addEventListener("close", () => this.onClose(), { signal });
      this.form.addEventListener("submit", (event) => this.onSubmit(event), { signal });
      this.inputs.forEach((input) =>
        input.addEventListener("input", () => {
          if (this.validated) this.validate(input);
        }, { signal })
      );
    }

    disconnectedCallback() {
      this.events?.abort();
      if (this.dialog?.open) this.dialog.close();
      document.documentElement.classList.remove(OPEN_CLASS);
    }

    open(opener) {
      if (this.dialog.open) return;
      this.opener = opener;
      this.openedAt = Date.now();
      // Fetch the SDK while the visitor writes; a failure here is retried on send
      loadSdk(this.dataset.emailjsSdk, this.dataset.emailjsSdkIntegrity).catch(() => {});

      const scrollbar = window.innerWidth - document.documentElement.clientWidth;
      document.documentElement.style.setProperty("--st-ffb-scrollbar", `${Math.max(scrollbar, 0)}px`);
      document.documentElement.classList.add(OPEN_CLASS);

      if (typeof this.dialog.showModal === "function") this.dialog.showModal();
      else this.dialog.setAttribute("open", "");

      if (!this.successView.hidden) {
        this.successTitle.focus();
      } else if (window.matchMedia("(hover: hover) and (pointer: fine)").matches) {
        // On touch screens, don't pop the keyboard up before the visitor has read the popup.
        this.inputs[0]?.focus();
      }
    }

    onClose() {
      document.documentElement.classList.remove(OPEN_CLASS);
      document.documentElement.style.removeProperty("--st-ffb-scrollbar");
      // A sent message has been seen: the next open starts a fresh form.
      if (!this.successView.hidden && !this.sending) this.showForm();
      this.opener?.focus({ preventScroll: true });
    }

    showForm() {
      this.successView.hidden = true;
      this.formView.hidden = false;
      this.clearAlert();
    }

    showSuccess() {
      this.form.reset();
      this.inputs.forEach((input) => this.setError(input, ""));
      this.validated = false;
      this.clearAlert();
      this.formView.hidden = true;
      this.successView.hidden = false;
      this.status.textContent = TEXT.sentStatus;
      if (this.dialog.open) this.successTitle.focus();
    }

    // ----- Validation -----

    validate(input) {
      const value = input.value.trim();
      const field = input.dataset.ffbInput;
      let message = "";
      if (!value) message = TEXT.required[field];
      else if (field === "email" && (!EMAIL_PATTERN.test(value) || input.validity.typeMismatch)) {
        message = TEXT.invalidEmail;
      }
      this.setError(input, message);
      return !message;
    }

    setError(input, message) {
      const error = this.form.querySelector(`[data-ffb-error="${input.dataset.ffbInput}"]`);
      error.textContent = message;
      error.hidden = !message;
      if (message) {
        input.setAttribute("aria-invalid", "true");
        input.setAttribute("aria-describedby", error.id);
      } else {
        input.removeAttribute("aria-invalid");
        input.removeAttribute("aria-describedby");
      }
    }

    // ----- Submission -----

    onSubmit(event) {
      event.preventDefault();
      if (this.sending || Date.now() - (this.failedAt || 0) < RETRY_GAP) return;

      this.validated = true;
      const invalid = this.inputs.filter((input) => !this.validate(input));
      if (invalid.length) {
        this.showAlert(TEXT.fixErrors);
        invalid[0].focus();
        return;
      }
      this.send();
    }

    value(field) {
      return this.inputs.find((input) => input.dataset.ffbInput === field).value.trim();
    }

    async send() {
      if (this.sending) return;
      this.clearAlert();
      this.setSending(true);

      try {
        // Spam trap filled, or sent faster than a person could: not sent
        if (this.trap?.value || Date.now() - (this.openedAt || 0) < MIN_FILL_TIME) {
          throw Object.assign(new Error("Blocked as automated"), { status: 403 });
        }
        if (Date.now() - lastSent() < THROTTLE) throw Object.assign(new Error("Sent moments ago"), { status: 429 });

        const emailjs = await loadSdk(this.dataset.emailjsSdk, this.dataset.emailjsSdkIntegrity);
        const params = {
          customer_name: this.value("name"),
          customer_email: this.value("email"),
          submitted_at: this.submittedAt(),
          customer_message: this.value("message"),
        };
        const response = await emailjs.send(this.dataset.emailjsService, this.dataset.emailjsTemplate, params, {
          publicKey: this.dataset.emailjsKey,
          blockHeadless: true,
        });
        if (response?.status !== 200) throw response;

        rememberSent();
        this.setSending(false);
        this.showSuccess();
      } catch (error) {
        // EmailJS rejects with { status, text }; status 0 is a network error
        console.warn("Founder feedback: message not sent.", error?.status, error?.text || error);
        this.failedAt = Date.now();
        this.setSending(false);
        const status = error?.status;
        if (status === 429) this.showAlert(TEXT.tooSoon);
        else if (status === 0 || status === undefined) this.showAlert(TEXT.failed);
        else this.showAlert(TEXT.unavailable);
      }
    }

    // Now, in the store's time zone (its UTC offset, e.g. +0530, comes from Liquid),
    // e.g. "4 November 2026 at 17:42 (UTC+05:30)".
    submittedAt() {
      const offset = /^([+-])(\d{2})(\d{2})$/.exec(this.dataset.utcOffset || "");
      const minutes = offset ? (offset[1] === "-" ? -1 : 1) * (Number(offset[2]) * 60 + Number(offset[3])) : 0;
      const storeTime = new Date(Date.now() + minutes * 60000).toLocaleString("en-GB", {
        timeZone: "UTC",
        day: "numeric",
        month: "long",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hourCycle: "h23",
      });
      return `${storeTime} (UTC${offset ? `${offset[1]}${offset[2]}:${offset[3]}` : ""})`;
    }

    setSending(sending) {
      this.sending = sending;
      this.submitButton.disabled = sending;
      this.submitButton.setAttribute("aria-busy", String(sending));
      this.submitLabel.textContent = sending ? TEXT.sending : this.submitLabel.dataset.label;
      this.inputs.forEach((input) => {
        input.readOnly = sending;
      });
      this.status.textContent = sending ? TEXT.sendingStatus : "";
    }

    showAlert(message) {
      this.alert.textContent = message;
      this.alert.hidden = false;
    }

    clearAlert() {
      this.alert.textContent = "";
      this.alert.hidden = true;
    }
  }

  customElements.define(TAG, SolethreadsFounderFeedback);
})();
